<template>
  <div class="max-h-[100dvh] overflow-auto min-h-[100dvh]">
    <TheDialogInfo
      :text="t('contact-support')"
      :title="t('title-delete-user')"
      :open="isShownDialogInfo"
      @dialog:close="isShownDialogInfo = false"
    />
    <TheHeaderBar
      class="flex items-center content-between w-full mt-16 lg:mt-0 bg-iq-grey-dark"
    >
      <AppCredits class="justify-end hidden w-full mr-4 lg:flex" />
    </TheHeaderBar>

    <TheProfileDetail
      @click:contact="showContactFormular = true"
      @delete:profile="onClickDeleteProfile"
    />

    <!-- DIALOGS -->
    <TheDialogConfirm
      :title="t('title-delete-user-confirm')"
      :text="t('text-delete-user-confirm')"
      :text-cancel="t('lbl-cancel')"
      :text-confirm="t('lbl-confirm')"
      :open="isShownDialogConfirm"
      @dialog:close="isShownDialogConfirm = false"
      @dialog:confirm="deleteProfile"
    />
    <TheDialogSupport
      :dialog-type="ContactRequestType.SUPPORT"
      :open="showContactFormular"
      @modal:close="showContactFormular = false"
    />
  </div>
</template>
<script setup lang="ts">
import { useReCaptcha } from 'vue-recaptcha-v3';
import { toast } from 'vue-sonner';
import { ContactRequestType } from '~/enum/contact-request-type.enum';
import { TierLevel } from '~/enum/tier-level.enum';
import { UserRole } from '~/enum/user-role.enum';

import type { OrganizationUser } from '~/interfaces/organization-user.interface';
definePageMeta({
  layout: 'logged-in',
  middleware: ['protected', 'stores-init'],
});
const { t } = useI18n({
  useScope: 'local',
});
const isShownDialogConfirm = ref(false);
const isShownDialogInfo = ref(false);
const recaptcha = useReCaptcha();
const showContactFormular = ref(false);
const userStore = useUserStore();
const { data: users, execute: getUser } = await useFetch<OrganizationUser[]>(
  `/api/users`,
  {
    immediate: false,
  }
);

async function deleteProfile() {
  isShownDialogConfirm.value = false;
  const organizationStore = useOrganizationStore();

  await recaptcha?.recaptchaLoaded();
  const token = await recaptcha?.executeRecaptcha('signup');

  if (!token) {
    clearError();
    return toast.warning(t('toast-delete-user'));
  }
  if (organizationStore.subscription?.tier?.level !== TierLevel.FREE) {
    await organizationStore.cancelSubscription(token).catch(() => {
      clearError();
      toast.warning(t('toast-cancel-sub'));
    });
  }
  trackAtServer('user_delete');
  clearWrittenData();
  await organizationStore.deleteOrganization().catch(() => {
    clearError();
    toast.warning(t('toast-delete-user'));
  });
  await navigateTo('/');
}

async function onClickDeleteProfile() {
  await getUser();

  if (!users?.value?.length) {
    //should never be the case, but just a guard for ts
    return;
  }
  if (userStore.user?.role === UserRole.SUPERADMIN && users.value?.length > 1) {
    isShownDialogInfo.value = true;
    return;
  }
  isShownDialogConfirm.value = true;
}

onUnmounted(() => {
  clearError();
});

useHead({
  title: t('head-title'),
});
</script>

<i18n lang="json">
{
  "de": {
    "contact-support": "Wende dich an den Support, um den Superadmin-Status erst an einen anderen User zu übertragen",
    "title-delete-user": "User löschen",
    "title-delete-user-confirm": "Wirklich löschen?",
    "text-delete-user-confirm": "Möchtest du den User wirklich löschen? Alle deine Daten gehen verloren.",
    "lbl-cancel": "ABBRECHEN",
    "lbl-confirm": "LÖSCHEN",
    "toast-delete-user": "Fehler beim löschen des Profils.",
    "toast-cancel-sub": "Fehler beim kündigen des Abonnements.",
    "head-title": "B2BSCAN - Profil"
  },
  "en": {
    "contact-support": "Contact support to transfer the Superadmin status to another user first",
    "title-delete-user": "Delete User",
    "title-delete-user-confirm": "Really delete?",
    "text-delete-user-confirm": "Do you really want to delete the user? All your data will be lost.",
    "lbl-cancel": "CANCEL",
    "lbl-confirm": "DELETE",
    "toast-delete-user": "Error deleting the profile.",
    "toast-cancel-sub": "Error canceling the subscription.",
    "head-title": "B2BSCAN - Profile"
  }
}
</i18n>
